import type { QueryKey } from '@tanstack/react-query';

import type { PageMessage } from '../../../types/PageMessage';

import type { MultiComboBoxProps } from './MultiComboBox';
import { PaginatedAsyncComboBoxBase } from './PaginatedAsyncComboBoxBase';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';

const DEFAULT_PAGINATED_DROPDOWN_MAX_HEIGHT = '608px';

interface PaginatedAsyncComboBoxOptionsProps<Option extends ComboBoxOption> {
    optionsQueryKey: (search: string) => QueryKey;
    optionsQueryFn: (params: { search: string; page: number }) => Promise<PageMessage<Option>>;
    customOptionsFilter?: (options: Option[], search: string) => Option[];
    prefetchOnHover?: boolean;
}

type PaginatedAsyncMultiComboBoxProps<Option extends ComboBoxOption = ComboBoxOption> = Omit<
    MultiComboBoxProps<Option>,
    'options'
> &
    PaginatedAsyncComboBoxOptionsProps<Option> & {
        selectedOptionsQueryKey: (values: Array<Option['value']>) => QueryKey;
        selectedOptionsQueryFn: (values: Array<Option['value']>) => Promise<Option[]>;
    };

export function PaginatedAsyncMultiComboBox<Option extends ComboBoxOption = ComboBoxOption>({
    value = [],
    optionsQueryKey,
    optionsQueryFn,
    customOptionsFilter,
    selectedOptionsQueryKey,
    selectedOptionsQueryFn,
    prefetchOnHover,
    onChange,
    isClearable = true,
    clearSearchOnSelect = true,
    dropdownMaxHeight = DEFAULT_PAGINATED_DROPDOWN_MAX_HEIGHT,
    ...props
}: PaginatedAsyncMultiComboBoxProps<Option>) {
    return (
        <PaginatedAsyncComboBoxBase<Option>
            {...props}
            mode={ComboBoxMode.Multi}
            value={value}
            optionsQueryKey={optionsQueryKey}
            optionsQueryFn={optionsQueryFn}
            selectedOptionsQueryKey={selectedOptionsQueryKey}
            selectedOptionsQueryFn={selectedOptionsQueryFn}
            customOptionsFilter={customOptionsFilter}
            isClearable={isClearable}
            dropdownMaxHeight={dropdownMaxHeight}
            clearSearchOnSelect={clearSearchOnSelect}
            prefetchOnHover={prefetchOnHover}
            onSelectionChange={(nextSelectedOptions, meta) => {
                onChange?.(
                    nextSelectedOptions.map((option) => option.value),
                    meta
                );
            }}
        />
    );
}
